import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { UseOutletContextQuote } from './Quote';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { QuoteDA } from '../../../../services/admin/quote.service';
import { Error } from '../../../../models/error.model';
import { CompanyQuotes } from '../../../../models/company.quotes.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { Quote } from '../../../../models/quote.model';
import { QuoteGroup } from '../../../../models/quote.group.model';
import { colorStatusQuote, colorValidUntil } from '../../../../scripts/badge.color.script';
import { accumulatedAmount, isVatSubject } from '../../../../scripts/financial.calculation.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { formatContact, formatMoney } from '../../../../scripts/format.value.script';
import { nameGroup } from '../../../../libraries/group.name.library';
import { statusDocumentValue } from '../../../../libraries/status.document.library';
import { getError } from '../../../../languages/translations/response';
import { sortColumn } from '../../../../scripts/table.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { usePaginationUrl } from '../../../../hooks/usePaginationUrl';
import { fetchDocumentPdf } from '../../../../scripts/document.pdf.script';
import { usePdfViewer } from '../../../../contexts/PdfViewerContext';
import { sanitizeFilename } from '../../../../scripts/document.filename.script';

import AppSaleListHeader from '../component/SaleListHeader';
import AppSortTable from '../../../../components/element/SortTable';
import AppPagination from '../../../../components/element/Pagination';
import AppPreloader from '../../../../components/Preloader';
import ButtonVouchers from '../../../../components/button/ButtonVouchers';

export interface QuoteListPageProps {};

let errorResponse: Error, quotesResponse: CompanyQuotes;

const QuoteListPage: React.FunctionComponent<QuoteListPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, contactsForCompany} = UseOutletContextQuote()
  const {lang} = StateLanguage()
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()

  const [mounted, setMounted] = useState(false)
  const [button, setButton] = useState({filter: false, group: false})
  const [filterState, setFilterState] = useState({interval: '', start_date: '', end_date: '', contact: '', contact_label: '', validity: '', status: '', expiration: '', payment: '', sort_field: '', sort_mode: '', group: '', search: ''})
  const filterRef = useRef(filterState)
  const filter = filterRef.current
  const setFilter = (v: React.SetStateAction<typeof filterState>) => { const n = typeof v === 'function' ? v(filterRef.current) : v; filterRef.current = n; setFilterState(n) }
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [quotes, setQuotes] = useState<Quote[] | null>(null)
  const [quoteGroups, setQuoteGroups] = useState<QuoteGroup[] | null>(null)
  const [storage, setStorage] = useState(null)
  const { openUrl } = usePdfViewer();
  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const vatSubject = isVatSubject(settingsForCompany, companyForUser);
  // Nombre de colonnes AVANT le bloc montants (TVA/total). La colonne TVA fait partie du bloc montants.
  const COLS_BEFORE_AMOUNTS = 6;

  const executeViewPdf = async (id: string, filename: string) => {
    if (!companyForUser) return;
    setPdfLoading(id);
    try {
      const url = await fetchDocumentPdf(companyForUser.id, 'quotes', id);
      openUrl(url, `${sanitizeFilename(filename)}.pdf`);
    } catch {
      // fetchDocumentPdf throws with a readable message on error
    } finally {
      setPdfLoading(null);
    }
  };

  const loadQuotes = async (id_company: string, page: number = 1) => {
    const f = filterRef.current
    await QuoteDA.getQuotes(id_company, f.interval, f.start_date, f.end_date, f.contact, f.status, f.expiration, f.sort_field, f.sort_mode, f.group, page, pagination.limit, f.search).then( (response) => {
      if (response.status === 200) {
        quotesResponse = response.data

        const filterData = {
          company_id: id_company,
          interval: f.interval,
          start_date: f.start_date,
          end_date: f.end_date,
          contact: f.contact,
          contact_label: f.contact_label,
          status: f.status,
          expiration: f.expiration,
          sort_field: f.sort_field,
          sort_mode: f.sort_mode,
          group: f.group,
          search: f.search
        }
        localStorage.setItem('filter_quotes', JSON.stringify(filterData))

        const groupBy = filterData.group
        const items = quotesResponse.items ?? quotesResponse.quotes ?? []
        const groups = quotesResponse.groups ?? []

        localStorage.setItem('nav_quotes', JSON.stringify({ company_id: id_company, ids: items.map((item: any) => item.id) }))

        if (!groupBy || groupBy === '') {
          setQuoteGroups([])
          setQuotes(items)

          const paginationData = quotesResponse.pagination
          if (paginationData) {
            const totalPages = paginationData.pages || 1
            const clampedPage = page > totalPages && totalPages >= 1 ? totalPages : paginationData.page || page
            let pagesOfPagination: number[] = []
            for (let i = 1; i <= totalPages; i++) { pagesOfPagination.push(i) }
            setPagination({...pagination, current: clampedPage, total: paginationData.total || 0, pages: pagesOfPagination})
            setUrlPage(clampedPage)
            if (page > totalPages && totalPages >= 1) {
              loadQuotes(id_company, totalPages)
              return
            }
          }
        } else {
          setQuotes([])
          setQuoteGroups(groups)
          setPagination({current: 1, limit: 20, total: 0, pages: [1]})
          setUrlPage(1)
        }

        setButton({filter: Boolean(filterData.interval || filterData.start_date || filterData.end_date || filterData.contact || filterData.status || filterData.expiration), group: Boolean(groupBy)})
        setStorage((localStorage.getItem('filter_quotes')) ? JSON.parse(localStorage.getItem('filter_quotes')!) : null)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      const newFilter = {...filterRef.current}
      sortColumn(item, newFilter)
      setFilter(newFilter)
      setUrlPage(1)
      loadQuotes(companyForUser.id, 1)
    }
  }

  const loadPagination = (current: number) => {
    if (!companyForUser) return null
    setUrlPage(current)
    loadQuotes(companyForUser.id, current)
  }

  useEffect( () => {
    let filter_quotes: any = (localStorage.getItem('filter_quotes')) ? JSON.parse(localStorage.getItem('filter_quotes')!) : null

    if (companyForUser) {
      const isMatch = filter_quotes && filter_quotes.company_id === companyForUser.id
      setFilter({
        interval: isMatch ? (filter_quotes.interval ?? '') : '', start_date: isMatch ? (filter_quotes.start_date ?? '') : '', end_date: isMatch ? (filter_quotes.end_date ?? '') : '',
        contact: isMatch ? (filter_quotes.contact ?? '') : '', contact_label: isMatch ? (filter_quotes.contact_label ?? '') : '', validity: '', status: isMatch ? (filter_quotes.status ?? '') : '', expiration: isMatch ? (filter_quotes.expiration ?? '') : '',
        payment: '', sort_field: isMatch ? (filter_quotes.sort_field ?? '') : '', sort_mode: isMatch ? (filter_quotes.sort_mode ?? '') : '', group: isMatch ? (filter_quotes.group ?? '') : '',
        search: isMatch ? (filter_quotes.search ?? '') : ''
      })

      setQuotes(null)
      setQuoteGroups(null)
      setButton({filter: false, group: false})
      loadQuotes(companyForUser.id, urlPage)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.quotes, branch: lang.labels.listQuotes}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && quotes && quoteGroups
        ?
        <div className="card card-flush">
          <AppSaleListHeader sale={"quote"} storage={storage} button={button} filter={filter} records={quotes} recordGroups={quoteGroups} setFilter={setFilter} loadDocuments={loadQuotes} totalItems={!button.group ? pagination.total : undefined}></AppSaleListHeader>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("created_date")}>
                          <span className="me-1">{lang.labels.documentDate}</span>
                          <AppSortTable attribute={"created_date"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-125px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("quote_number")}>
                          <span className="me-1">{lang.labels.quoteNumber}</span>
                          <AppSortTable attribute={"quote_number"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("concerning")}>
                          <span className="me-1">{lang.labels.concerning}</span>
                          <AppSortTable attribute={"concerning"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("customer")}>
                          <span className="me-1">{lang.labels.customer}</span>
                          <AppSortTable attribute={"customer"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("status")}>
                          <span className="me-1">{lang.labels.status}</span>
                          <AppSortTable attribute={"status"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("expiration")}>
                          <span className="me-1">{lang.labels.expiration}</span>
                          <AppSortTable attribute={"expiration"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      {vatSubject && <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.tax_total")}>
                          <span className="me-1">{lang.labels.vatAmount}</span>
                          <AppSortTable attribute={"amount.tax_total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>}
                      <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.total")}>
                          <span className="me-1">{lang.labels.amountTotal}</span>
                          <AppSortTable attribute={"amount.total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-50px text-center"></th>
                      <th className="w-50px text-center"></th>
                    </tr>
                  </thead>
                  { quotes.length > 0 || quoteGroups.length > 0
                    ?
                    <>
                      { button.group
                        ?
                        <>
                          { quoteGroups.map (( (item, index) => { return (
                            <tbody key={index} className="text-secondary fw-bold">
                              <tr>
                                <td colSpan={9 + (vatSubject ? 1 : 0)} className="text-uppercase fw-bolder fs-7">
                                  <div className="d-flex align-items-center">
                                    <span className="svg-icon svg-icon-3 svg-icon-gray-900 me-5">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M22 12C22 12.9 21.9 13.8 21.7 14.6L5 4.89999C6.8 3.09999 9.3 2 12 2C17.5 2 22 6.5 22 12Z" fill="black"/>
                                        <path opacity="0.3" d="M3.7 17.5C2.6 15.9 2 14 2 12C2 9.20003 3.1 6.70002 5 4.90002L9.3 7.40002V14.2L3.7 17.5ZM17.2 12L5 19.1C6.8 20.9 9.3 22 12 22C16.6 22 20.5 18.8 21.7 14.6L17.2 12Z" fill="black"/>
                                      </svg>
                                    </span>
                                    <span className="me-3">{lang.labels.group} {index + 1}:</span>
                                    {nameGroup(lang, filter.group, item.group_detail)}
                                  </div>
                                </td>
                              </tr>
                              { item.quotes.map (( (sub_item, sub_index) => { return (
                                <tr key={sub_index} className="row-dashed">
                                  <td className="text-nowrap">{dateFormat(lang.code, sub_item.created_date)}</td>
                                  <td>
                                    <Link to={"/main/sale/quote/view/" + sub_item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{sub_item.quote_number}</Link>
                                  </td>
                                  <td>{sub_item.concerning}</td>
                                  <td>{formatContact(sub_item.customer)}</td>
                                  <td>
                                    <span className={`badge badge-light-${colorStatusQuote(sub_item.status)} fs-7`}>{statusDocumentValue(lang, sub_item.status)}</span>
                                  </td>
                                  <td>
                                    <span className={`badge badge-light-${colorValidUntil(sub_item.status, sub_item.valid_until)} fs-7`}>{dateFormat(lang.code, sub_item.valid_until)}</span>
                                  </td>
                                  {vatSubject && <td className="text-end text-nowrap">{sub_item.currency.code} {formatMoney(sub_item.amount?.tax_total ?? 0)}</td>}
                                  <td className="text-end text-nowrap">
                                    {sub_item.currency.code} {formatMoney(sub_item.amount.total)}
                                    {sub_item.foreign_currency?.code && <div className="fs-9 fw-normal text-muted">{sub_item.foreign_currency.code} {formatMoney(sub_item.foreign_currency.amount?.total ?? 0)}</div>}
                                  </td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-icon btn-light-primary"
                                      title={lang.labels.view}
                                      onClick={() => executeViewPdf(sub_item.id, sub_item.quote_number)}
                                      disabled={pdfLoading === sub_item.id}
                                    >
                                      {pdfLoading === sub_item.id
                                        ? <span className="spinner-border spinner-border-sm"></span>
                                        : <i className="bi bi-eye fs-5"></i>
                                      }
                                    </button>
                                  </td>
                                  <td className="text-center">
                                    <ButtonVouchers vouchers={sub_item.vouchers} />
                                  </td>
                                </tr>
                              )}))}
                              <tr className={`fw-bolder fs-7 ${index < quoteGroups.length - 1 && "row-dashed"}`}>
                                <td colSpan={COLS_BEFORE_AMOUNTS}></td>
                                {vatSubject && <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(item.quotes).tax)}</td>}
                                <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(item.quotes).total)}</td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tbody>
                          )}))}
                        </>
                        :
                        <>
                          <tbody className="text-secondary fw-bold">
                            { quotes.map (( (item, index) => { return (
                              <tr key={index} className="row-dashed">
                                <td className="text-nowrap">{dateFormat(lang.code, item.created_date)}</td>
                                <td>
                                  <Link to={"/main/sale/quote/view/" + item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{item.quote_number}</Link>
                                </td>
                                <td>{item.concerning}</td>
                                <td>{formatContact(item.customer)}</td>
                                <td>
                                  <span className={`badge badge-light-${colorStatusQuote(item.status)} fs-7`}>{statusDocumentValue(lang, item.status)}</span>
                                </td>
                                <td>
                                  <span className={`badge badge-light-${colorValidUntil(item.status, item.valid_until)} fs-7`}>{dateFormat(lang.code, item.valid_until)}</span>  
                                </td>
                                {vatSubject && <td className="text-end text-nowrap">{item.currency.code} {formatMoney(item.amount?.tax_total ?? 0)}</td>}
                                <td className="text-end text-nowrap">
                                  {item.currency.code} {formatMoney(item.amount.total)}
                                  {item.foreign_currency?.code && <div className="fs-9 fw-normal text-muted">{item.foreign_currency.code} {formatMoney(item.foreign_currency.amount?.total ?? 0)}</div>}
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon btn-light-primary"
                                    title={lang.labels.view}
                                    onClick={() => executeViewPdf(item.id, item.quote_number)}
                                    disabled={pdfLoading === item.id}
                                  >
                                    {pdfLoading === item.id
                                      ? <span className="spinner-border spinner-border-sm"></span>
                                      : <i className="bi bi-eye fs-5"></i>
                                    }
                                  </button>
                                </td>
                                <td className="text-center">
                                  <ButtonVouchers vouchers={item.vouchers} />
                                </td>
                              </tr>
                            )}))}
                          </tbody>
                          <tfoot>
                            <tr className="fw-bolder fs-7">
                              <td colSpan={COLS_BEFORE_AMOUNTS}></td>
                              {vatSubject && <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(quotes).tax)}</td>}
                              <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(quotes).total)}</td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </>
                      }
                    </>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={9 + (vatSubject ? 1 : 0)} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              {!button.group && <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>}
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default QuoteListPage;
